'use client';

import { useState } from 'react';
import styled from 'styled-components';

import { Close } from '@zoomit/icons';
import type { SpacingType } from '@zoomit/primitives';
import { Flex, Spacing, spacingHelper } from '@zoomit/primitives';
import { cssVar, Hr, theme, Typography } from '@zoomit/zoomit-ds';

import { onAdCloseTag } from '../../helpers/events';
import type { DisplayModule } from '../../types/modules';
import DisplayParser from './DisplayParser';

type Props = {
  module: DisplayModule;
  closeButtonKind: 'general' | 'on-display';
  startDisplayIndex?: number;
  spacing?: SpacingType;
};

const AdvertisingParser = (props: Props) => {
  const { module, startDisplayIndex = 0, closeButtonKind = 'general', spacing } = props;
  const [showDisplays, setShowDisplays] = useState(true);

  const columnCount = module.bannerSpec.device === 'Phone' ? 1 : module.bannerSpec.columnCount;

  const onDisplayClose = () => {
    setShowDisplays(false);
    onAdCloseTag(module.zoneCode);
  };

  const hasDisplay = module.displays && module.displays.length > startDisplayIndex;

  const sharpBorderedPositionIds = ['topBar', 'bottomSticky_mobile'];

  const hasBorderRadius = !sharpBorderedPositionIds.includes(module.position.positionId);

  return showDisplays ? (
    <Root spacing={spacing}>
      {closeButtonKind === 'general' && (
        <CloseButtonWrapper
          $hasDisplay={!!hasDisplay}
          flexWrap="nowrap"
          alignItems="center"
          gap={Spacing.x}
          spacing={{ mb: 'x' }}
        >
          <Close onClick={onDisplayClose} fill={cssVar(theme.grey1)} className="cursor-pointer" />
          <Typography variant="subhead" textColor={cssVar(theme.grey1)}>
            تبلیغات
          </Typography>
          <Hr />
        </CloseButtonWrapper>
      )}

      <Grid $rowCount={module.bannerSpec.rowCount} $columnCount={columnCount}>
        {Array.from({ length: columnCount * module.bannerSpec.rowCount })
          .slice(0, module.displays?.length)
          .map((_, index) => (
            <DisplayParser
              hasBorderRadius={hasBorderRadius}
              module={module}
              displayIndex={index + startDisplayIndex}
              key={index}
              hasCloseButton={closeButtonKind === 'on-display'}
            />
          ))}
      </Grid>
    </Root>
  ) : (
    <></>
  );
};

export default AdvertisingParser;

const Root = styled.div<{ spacing?: SpacingType }>`
  width: 100%;
  ${({ spacing }) => spacing && spacingHelper(spacing)};

  &:has(> :empty) {
    display: none;
  }
`;

const Grid = styled.div<{ $rowCount: number; $columnCount: number }>`
  display: ${({ $columnCount, $rowCount }) => ($rowCount === 1 && $columnCount === 1 ? 'initial' : 'grid')};
  grid-template-columns: ${({ $columnCount }) => `repeat(${$columnCount}, 1fr)`};
  grid-template-rows: ${({ $rowCount }) => `repeat(${$rowCount}, auto)`};
  grid-column-gap: 2.4rem;
  grid-row-gap: 2.4rem;
`;

const CloseButtonWrapper = styled(Flex)<{ $hasDisplay: boolean }>`
  visibility: ${({ $hasDisplay }) => ($hasDisplay ? 'visible' : 'hidden')};
`;
